import { useCallback, useMemo } from 'react';
import { Button, Checkbox } from '@origo/origo-ui';
import type { Område } from '@origo/seksjon';
import { useAppDispatch, useAppSelector } from '../../../store/storeHooks';
import {
    selectExcludedDelomradetyper,
    toggleDelomradetype,
    setExcluded,
} from '../../../store/delomradetypeFilterSlice';
import styles from './DelomradetypeFilterPanel.module.css';

interface DelomradetypeFilterPanelProps {
    /** Toppnivå-områdene fra useAdminAreas — én rad per delområdetype-gruppe i planen. */
    areas: readonly Område[];
}

export function DelomradetypeFilterPanel({ areas }: Readonly<DelomradetypeFilterPanelProps>) {
    const dispatch = useAppDispatch();
    const excluded = useAppSelector(selectExcludedDelomradetyper);
    const excludedSet = useMemo(() => new Set(excluded), [excluded]);

    // `areas` kommer ferdig sortert (numerisk på navn) fra useAdminAreas.
    const hiddenInPlan = areas.filter((a) => excludedSet.has(a.id)).length;
    const visibleInPlan = areas.length - hiddenInPlan;

    const showAll = useCallback(() => {
        const planIds = new Set(areas.map((a) => a.id));
        dispatch(setExcluded(excluded.filter((id) => !planIds.has(id))));
    }, [areas, excluded, dispatch]);

    const hideAll = useCallback(() => {
        const next = new Set(excluded);
        for (const area of areas) next.add(area.id);
        dispatch(setExcluded([...next]));
    }, [areas, excluded, dispatch]);

    if (areas.length === 0) {
        return (
            <p className={styles.empty}>
                Planen har ingen delområdetyper å filtrere på.
            </p>
        );
    }

    return (
        <div className={styles.root}>
            <p className={styles.help}>
                Huk av delområdetyper for å skjule dem fra delområde-treet. Valget huskes
                på tvers av planer.
            </p>

            <fieldset className={styles.list}>
                <legend className="sr-only">Delområdetyper</legend>
                {areas.map((area) => {
                    const isExcluded = excludedSet.has(area.id);
                    return (
                        <Checkbox
                            key={area.id}
                            label={area.navn}
                            checked={!isExcluded}
                            onChange={() => dispatch(toggleDelomradetype(area.id))}
                        />
                    );
                })}
            </fieldset>

            <div className={styles.footer}>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={showAll}
                    disabled={hiddenInPlan === 0}
                >
                    Vis alle{hiddenInPlan > 0 ? ` (${hiddenInPlan} skjult)` : ''}
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={hideAll}
                    disabled={visibleInPlan === 0}
                >
                    Skjul alle
                </Button>
            </div>
        </div>
    );
}
